import * as Sentry from '@sentry/browser'
import type { EventHint, Integration } from '@sentry/core'
import type { TwyfordSentryConfig } from './types'
import { validateConfig } from '@/utils/validator'
import { logger } from '@/utils/logger'
import { defaultIgnoreErrors, companyAllowUrls } from './config'
import { createApp } from 'vue'
import App from '../App.vue'
import { FloatingMenu } from '../plugins/floatingMenu'
import { setSentryUrl } from '@/config'
import { ElConfigProvider } from 'element-plus'
import { setLocale } from '@/locales'

let vm: any = null
let lastEvent: any
let _Sentry: typeof Sentry | null = null

export const feedbackEvent = 'user_sentry_feedback'
export const TwyfordSentryLoadedEvent = 'TwyfordSentryLoaded'

export let shawdowEl: any

export function getShadow() {
  return shawdowEl
}

export function getSentry() {
  return _Sentry
}
export function getLastEvent() {
  return lastEvent
}

// 获取脚本属性配置,自动初始化
function getConfigFromScriptAttributes() {
  const script = document.querySelector('script[data-sentry-dsn]')
  if (!script) {
    return null
  }

  const config: Record<string, any> = {}

  Array.from(script.attributes).forEach((attr) => {
    if (!attr.name.startsWith('data-sentry-')) return

    // 转换 kebab-case 为 camelCase
    const key = attr.name
      .replace('data-sentry-', '')
      .replace(/-([a-z])/g, (_, char) => char.toUpperCase())

    config[key] = parseValue(attr.value)
  })

  return Object.keys(config).length > 0 ? config : null
}

/**
 * 智能解析字符串值为其原始类型（布尔值、数字、JSON对象/数组），
 * 并特殊处理非标准的正则表达式数组格式 (如 "[/pattern1/, /pattern2/]")。
 *
 * @param value 需要解析的输入字符串。
 * @returns 解析后的值 (string, boolean, number, object, array, or RegExp array)。
 */
function parseValue(value: string): any {
  const trimmed = value.trim()

  // 如果字符串为空，直接返回空字符串
  if (trimmed === '') {
    return trimmed
  }

  // 1. 布尔值
  if (trimmed === 'true') return true
  if (trimmed === 'false') return false

  // 2. 数字
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
    return Number(trimmed)
  }

  // 3. 特殊处理：正则表达式数组格式 (如 "[/abc/, /123/]")
  // 这种格式不是标准 JSON，需要自定义解析逻辑。
  if (
    trimmed.startsWith('[') &&
    trimmed.endsWith(']') &&
    trimmed.includes('/')
  ) {
    // 移除首尾的方括号
    const content = trimmed.slice(1, -1).trim()

    // 匹配正则数组
    const regexStrings = content
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0)

    try {
      const regexArray = regexStrings.map((rStr) => {
        // 确保每个子项确实是 /.../ 格式
        if (rStr.startsWith('/') && rStr.endsWith('/')) {
          // 提取内容 (移除首尾的 '/')
          const pattern = rStr.slice(1, -1)
          // 目前只支持 pattern
          return new RegExp(pattern)
        } else {
          // 如果子项格式不正确，抛出错误，回退到普通字符串
          throw new Error('Invalid regex format within array')
        }
      })

      return regexArray // 成功解析为 RegExp[]
    } catch {
      // 解析失败 (例如格式不完全正确)，回退到下一步 JSON 解析
    }
  }

  // 4. JSON 数组或对象 (例如标准 JSON 格式的字符串数组: '["/abc/"]')
  if (
    (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
    (trimmed.startsWith('{') && trimmed.endsWith('}'))
  ) {
    try {
      // 尝试解析为标准的 JSON。这会捕获 `{}` 或 `[]` 或 `["abc"]`
      return JSON.parse(trimmed)
    } catch {
      // 解析失败返回原字符串
      return trimmed
    }
  }

  // 5. 默认返回原字符串
  return trimmed
}

export class TwyfordSentrySDK {
  private initialized = false
  private config: TwyfordSentryConfig | null = null
  private floatingMenu: FloatingMenu | null = null

  logs: any[] = [] // 记录一些信息

  /**
   * 初始化 Sentry
   */
  init(config: TwyfordSentryConfig): typeof Sentry | null {
    if (!vm) {
      vm = createApp(App)
      // vm.provide('ElConfigProvider', {
      //   namespace: 'es'
      // })
      vm.component('ElConfigProvider', ElConfigProvider)
      // 初始化 Vue 实例, #sentry-app就创建
      if (!document.getElementById('sentry-app')) {
        const appContainer = document.createElement('div')
        appContainer.id = 'sentry-app'
        document.body.appendChild(appContainer)
      }

      // 2. 创建 Shadow DOM
      const host = document.getElementById('sentry-app')
      if (host) {
        // 设置host样式 relative和z-index
        host.style.position = 'relative'
        host.style.zIndex = '9999'
        //   const shadow = host.attachShadow({ mode: 'open' })
        //   shawdowEl = shadow
        //   vm.mount(shadow)
      }

      vm.mount('#sentry-app')
    }

    if (this.initialized) {
      logger.warn('Sentry already initialized')
      return Sentry
    }

    // 验证配置
    const validationError = validateConfig(config)
    if (validationError) {
      logger.error('Invalid config:', validationError)
      return null
    }

    this.config = config
    setSentryUrl(
      config.environment === 'production'
        ? import.meta.env.VITE_APP_SENTRY_PROD__SEND_URL
        : import.meta.env.VITE_APP_SENTRY_TEST_SEND_URL
    )
    // 合并默认配置
    const sentryConfig: Sentry.BrowserOptions = {
      dsn: config.dsn,
      environment: config.environment,
      release: config.release || `${config.appName}@${config.appVersion}`,

      // 采样率
      tracesSampleRate: config.tracesSampleRate ?? 0.1,
      sampleRate: config.sampleRate ?? 1.0, // 默认采样率
      replaysSessionSampleRate: config.replaysSessionSampleRate ?? 0,
      replaysOnErrorSampleRate: config.replaysOnErrorSampleRate ?? 1.0,

      // 错误过滤
      ignoreErrors: [...defaultIgnoreErrors, ...(config.ignoreErrors || [])],
      normalizeDepth: config.normalizeDepth || 3, // 默认采样深度3

      // allowUrls才会触发beforeSend
      allowUrls: config.allowUrls || companyAllowUrls,
      denyUrls: config.denyUrls,

      // 集成配置
      integrations: this.getIntegrations(config),

      // 钩子函数
      beforeSend: this.createBeforeSend(config)
      //   beforeBreadcrumb: config.beforeBreadcrumb
    }

    Sentry.init(sentryConfig)

    // 设置用户信息
    if (config.userId || config.userName || config.userEmail || config.system) {
      Sentry.setUser({
        id: config.userId,
        username: config.userName,
        email: config.userEmail,
        system: config.system
      })
    }

    // 设置自定义标签和上下文
    if (config.customTags) {
      Object.entries(config.customTags).forEach(([key, value]) => {
        Sentry.setTag(key, String(value))
      })
    }

    if (config.customContext) {
      Sentry.setContext('business', config.customContext)
    }

    // 设置公司级别标签
    Sentry.setTag('app_name', config.appName)
    Sentry.setTag('app_version', config.appVersion)

    this.initialized = true
    logger.info('Sentry initialized successfully')

    _Sentry = Sentry

    window.dispatchEvent(new CustomEvent(TwyfordSentryLoadedEvent)) // 触发loaded事件

    return Sentry
  }

  /**
   * 获取集成配置
   */
  private getIntegrations(config: TwyfordSentryConfig): Integration[] {
    const integrations: Integration[] = [
      Sentry.browserTracingIntegration(),
      Sentry.httpClientIntegration()
    ]

    if (config.enableReplay) {
      // 在 Sentry v7 中，Replay 的配置选项可能略有不同
      integrations.push(
        Sentry.replayIntegration({
          maskAllText: config.maskAllText || false,
          blockAllMedia: true,
          maxReplayDuration: config.maxReplayDuration || 60000,
          minReplayDuration: config.minReplayDuration || 10000
        })
      )
    }

    if (config.enableReplay) {
      integrations.push(
        Sentry.feedbackIntegration({
          colorScheme: 'system',
          autoInject: false
        })
      )
    }

    return integrations
  }

  /**
   * 创建 beforeSend 钩子
   */
  private createBeforeSend(
    config: TwyfordSentryConfig
  ): (event: Sentry.ErrorEvent, hint: EventHint) => Sentry.ErrorEvent | null {
    return (event, hint) => {
      // 开发环境不上报
      if (config.environment === 'development') {
        logger.debug('Event captured (dev mode):', event)
        return null
      }

      // 添加业务上下文
      event.contexts = {
        ...event.contexts,
        company: {
          app_name: config.appName,
          app_version: config.appVersion,
          timestamp: Date.now()
        }
      }

      // 执行用户自定义的 beforeSend
      if (config.beforeSend) {
        return config.beforeSend(event, hint)
      }

      lastEvent = event

      window.dispatchEvent(
        new CustomEvent(feedbackEvent, {
          detail: { event }
        })
      )

      return event
    }
  }

  /**
   * 设置语言
   * @param locale 当前语言
   */
  setLocale(locale: string): void {
    setLocale(locale)
    this.floatingMenu?.refreshTemplate()
  }

  /**
   * 设置悬浮窗实例
   * @param floatingMenu 悬浮窗实例
   */
  setFloatingMenu(floatingMenu: FloatingMenu): void {
    this.floatingMenu = floatingMenu
  }

  getSentry() {
    return Sentry
  }

  /**
   * 设置用户信息
   */
  setUser(user: Sentry.User | null): void {
    Sentry.setUser(user)
  }

  /**
   * 手动捕获错误
   */
  captureError(error: Error, context?: Record<string, any>): string {
    if (context) {
      Sentry.setContext('error_context', context)
    }
    return Sentry.captureException(error)
  }

  /**
   * 手动捕获消息
   */
  captureMessage(
    message: string,
    level: Sentry.SeverityLevel = 'info'
  ): string {
    return Sentry.captureMessage(message, level)
  }

  /**
   * 添加面包屑
   */
  addBreadcrumb(breadcrumb: Sentry.Breadcrumb): void {
    Sentry.addBreadcrumb(breadcrumb)
  }

  /**
   * 设置标签
   */
  setTag(key: string, value: string): void {
    Sentry.setTag(key, value)
  }

  /**
   * 设置上下文
   */
  setContext(name: string, context: Record<string, any> | null): void {
    Sentry.setContext(name, context)
  }

  /**
   * 获取当前配置
   */
  getConfig(): TwyfordSentryConfig | null {
    return this.config
  }

  /**
   * 检查是否已初始化
   */
  isInitialized(): boolean {
    return this.initialized
  }
}

export const TwyfordSentry = new TwyfordSentrySDK()
window.TwyfordSentry = TwyfordSentry // 暴露sdk实例

// 如果页面上有script标签包含data-sentry-dsn等属性，自动初始化
tryInit()
function tryInit() {
  const config = getConfigFromScriptAttributes() as TwyfordSentryConfig
  if (config && !validateConfig(config)) {
    TwyfordSentry.init(config)
  }
}
